import { useState } from "react";
import SearchSelect from "../SearchSelect";
import Modal from "../ui/Modal";

const QuoteModal = ({ quote, clients, settings, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    id: quote?._id || '',
    clientId: quote?.clientId || '',
    date: quote?.date || new Date().toISOString().split('T')[0],
    validUntil: quote?.validUntil || '',
    status: quote?.status || 'pending',
    items: quote?.items || [{ itemName: '', description: '', quantity: 1, price: 0 }],
    notes: quote?.notes || '',
    discountPercentage: quote?.discountPercentage || 0
  });

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemName: '', description: '', quantity: 1, price: 0 }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const clientOptions = clients.map(client => ({
    value: client._id,
    label: client.name + (client.contact_person ? ` - ${client.contact_person}` : '')
  }));

  const handleClientChange = (value, name) => {
    setFormData({ ...formData, clientId: value });
  };

  const formatCurrency = (amount) => `${settings.currency}${parseFloat(amount || 0).toFixed(2)}`;

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  const discount = subtotal * (formData.discountPercentage || 0) / 100;
  const afterDiscount = subtotal - discount;
  const tax = settings.taxExempt ? 0 : afterDiscount * (settings.taxPercentage || 10) / 100;
  const total = afterDiscount + tax;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={quote ? 'Edit Quote' : 'Create Quote'}
      size="xlarge"
      footer={
        <div className="flex gap-3">
          <button 
            type="button" 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={handleSubmit}
          >
            {quote ? 'Update Quote' : 'Create Quote'}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="p-0">
          {/* Top Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <SearchSelect
                data={clientOptions}
                selectedValue={formData.clientId}
                onChange={handleClientChange}
                placeholder="Select or search client..."
                name="clientId"
                className="mb-3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
                <option value="converted">Converted</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
              <div className="px-3 py-2 text-lg font-bold text-blue-600 bg-gray-50 rounded-md">
                {formatCurrency(total)}
              </div>
            </div>
          </div>

          {/* Date Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quote Date</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.validUntil}
                onChange={(e) => setFormData({ ...formData, validUntil: e.target.value })}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Items and Notes */}
            <div className="lg:col-span-2">
              {/* Line Items */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <label className="block text-sm font-medium text-gray-700">Line Items</label>
                  <button 
                    type="button" 
                    onClick={addItem} 
                    className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <i className="fas fa-plus mr-1"></i> Add Item
                  </button>
                </div>

                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Qty</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Price</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {formData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <input
                              type="text"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded mb-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Item"
                              value={item.itemName}
                              onChange={(e) => updateItem(index, 'itemName', e.target.value)}
                              required
                            />
                            <textarea
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Description"
                              rows="2"
                              value={item.description}
                              onChange={(e) => updateItem(index, 'description', e.target.value)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <input
                              type="number"
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Price"
                              value={item.price}
                              onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <span className="font-medium text-gray-900">
                              {formatCurrency(item.quantity * item.price)}
                            </span>
                          </td>
                          <td className="px-4 py-3 align-middle">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={formData.items.length === 1}
                            >
                              <i className="fas fa-trash text-sm"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes, terms, or conditions..."
                />
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
                  <h6 className="text-sm font-medium text-gray-900 mb-0">Quote Summary</h6>
                </div>
                <div className="p-4">
                  {/* Discount */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Discount (%)</label>
                    <div className="flex">
                      <input
                        type="number"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={formData.discountPercentage}
                        onChange={(e) => setFormData({ ...formData, discountPercentage: parseFloat(e.target.value) || 0 })}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                      <span className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 rounded-r-md">%</span>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    {/* Subtotal */}
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-600">Subtotal:</span>
                      <span className="text-sm font-medium text-gray-900">{formatCurrency(subtotal)}</span>
                    </div>

                    {/* Discount */}
                    {formData.discountPercentage > 0 && (
                      <>
                        <div className="flex justify-between items-center mb-2 text-red-600">
                          <span className="text-sm">Discount ({formData.discountPercentage}%):</span>
                          <span className="text-sm font-medium">-{formatCurrency(discount)}</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">After Discount:</span>
                          <span className="text-sm font-medium text-gray-900">{formatCurrency(afterDiscount)}</span>
                        </div>
                      </>
                    )}

                    {/* Tax */}
                    {!settings.taxExempt && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Tax ({settings.taxPercentage}%):</span>
                        <span className="text-sm font-medium text-gray-900">{formatCurrency(tax)}</span>
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-2 mt-2">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-semibold text-gray-900">Total:</span>
                        <span className="text-xl font-bold text-blue-600">{formatCurrency(total)}</span>
                      </div>
                    </div>

                    {settings.taxExempt && (
                      <p className="text-xs text-gray-500 mt-2">Tax exempt business</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default QuoteModal;